import React, { useEffect, useState, useRef } from "react";
import "@google/model-viewer";
import "./Ar.css";
import { useTranslation } from "react-i18next";
import { useSEO } from "../../context/SEOContext";
import { loadProductsWithLangFallback } from "../../utils/productLoader";

function Ar() {
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true); // API loading
  const [error, setError] = useState(null);
  const [modelLoading, setModelLoading] = useState(true); // GLB loading

  const modelRef = useRef(null); // 👈 model-viewer referansı
  const setSeo = useSEO();
  useEffect(() => {
    if (product) {
      setSeo({
        title: `AR - ${product.name} | Mükemel`,
        description: product.metadesc || "",
        keywords: product.metakey || "",
      });
    }
  });

  // URL'den /ar/29 gibi id'yi al
  const getIdFromUrl = () => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && /^\d+$/.test(last)) {
      return Number(last);
    }
    return null;
  };

  useEffect(() => {
    const id = getIdFromUrl();

    if (id == null) {
      setError("Geçersiz URL. /ar/29 gibi bir adres kullanmalısın.");
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);

        // Şu anki site dili (en, tr, ar vs.)
        const lang = i18n.language || "en";

        // Bu fonksiyon:
        // - her zaman EN ürünlerini çekiyor
        // - seçili dil için (tr/ar) varsa üzerine yazıyor
        const products = await loadProductsWithLangFallback(lang);

        // ID'si URL'deki ID'ye uyan ürünü bul
        const p =
          products.find((item) => Number(item.id) === id) ||
          products.find((item) => Number(item.stock_code) === id);

        if (!p) {
          setError("Bu ID'ye ait ürün bulunamadı.");
          setProduct(null);
        } else {
          setProduct(p);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Bilinmeyen bir hata oluştu");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    // 👇 Dil değişince aynı ürünü o dile göre tekrar yükle
  }, [i18n.language]);

  // Ürün her değiştiğinde model için loading'i tekrar true yap
  useEffect(() => {
    if (product && product.ar_url && String(product.ar_url).trim().length > 0) {
      setModelLoading(true);
    } else {
      setModelLoading(false);
    }
  }, [product]);

  // model-viewer yüklenince / hata alınca event dinleyicisi
  useEffect(() => {
    const el = modelRef.current;
    if (!el) return;

    const handleLoad = () => {
      console.log("model loaded");
      setModelLoading(false);
    };

    const handleError = () => {
      console.log("model load error");
      setModelLoading(false);
    };

    el.addEventListener("load", handleLoad);
    el.addEventListener("error", handleError);

    return () => {
      el.removeEventListener("load", handleLoad);
      el.removeEventListener("error", handleError);
    };
  }, [product]); // product değişince yeniden bağla

  if (loading) {
    // API çağrısı devam ederken
    return (
      <div className="container">
        <div className="row justify-content-center my-5">
          <div className="col-10 col-md-8">
            <div className="alert alert-info text-center" role="alert">
              The product is loading… Please wait.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: "red", fontFamily: "sans-serif" }}>
        {error}
      </div>
    );
  }

  if (!product) {
    // API bitti ama ürün yoksa
    return (
      <div className="container">
        <div className="row justify-content-center my-5">
          <div className="col-10 col-md-8">
            <div className="alert alert-danger text-center" role="alert">
              The AR file for this product could not be found.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasArUrl = product.ar_url && String(product.ar_url).trim().length > 0;

  return (
    <div
      style={{
        height: "100vh",
        position: "relative",
        marginBottom: 40,
      }}
    >
      {/* Üstte ürün başlığı */}
      <div className="container mt-5">
        <div className="row">
          <div className="col-lg-12 text-center">
            <p className="text-uppercase text-center fs-11 pt-5 mb-0 fw-lighter">
              {t("ProductTitle")}
            </p>
            <h3 className="text-center pt-0 pb-4 fw-light">
              {product.name || `Ürün #${product.id}`}
            </h3>
          </div>
        </div>
      </div>

      {hasArUrl ? (
        <>
          {/* 3D / AR alanı */}
          <model-viewer
            ref={modelRef} // 👈 ref bağladık
            src={product.ar_url}
            ar
            ar-modes="webxr scene-viewer quick-look"
            ar-placement="floor"
            ar-scale="auto"
            camera-controls
            environment-image="neutral"
            shadow-intensity="1"
            touch-action="pan-y"
            style={{
              width: "100%",
              height: "70vh",
              maxHeight: "600px",
            }}
          >
            <button
              slot="ar-button"
              className="text-white"
              style={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                padding: "15px 35px",
                borderRadius: 4,
                backgroundColor: "#381615",
                border: "none",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("PlaceinAR")}
            </button>
          </model-viewer>

          {/* GLB yüklenirken beyaz ekran yerine gösterilecek uyarı */}
          {modelLoading && (
            <div className="d-flex align-items-center justify-content-center w-100 h-100 position-absolute top-0 start-0">
              <div className="alert alert-info text-center mb-0" role="alert">
                The product is loading… Please wait.
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="d-flex align-items-center justify-content-center w-100 h-100">
          <div className="alert alert-danger text-center mb-0" role="alert">
            The AR file for this product could not be found.
          </div>
        </div>
      )}
    </div>
  );
}

export default Ar;
